import { Alert, Button, Link } from "@zapier/design-system";
import { useFormatter, useTranslations } from "@zapier/i18n";
import type { AssetLoadError } from "@zapier/client";
import { AssetTypes } from "@zapier/utils";
import styles from "./AssetLoadErrorAlert.module.css";

interface Props {
  error?: AssetLoadError;
  assetTypes?: readonly AssetTypes[];
}

export function AssetLoadErrorAlert({ error, assetTypes = [] }: Props) {
  const t = useTranslations("asset-load-error");
  const tAssetTypes = useTranslations("asset-types.plural");
  const format = useFormatter();

  if (!error) return undefined;

  const labels: Partial<Record<AssetTypes, string>> = {
    [AssetTypes.Zap]: tAssetTypes("zap"),
    [AssetTypes.NextGenZap]: tAssetTypes("nextGenZap"),
    [AssetTypes.Table]: tAssetTypes("table"),
    [AssetTypes.Canvas]: tAssetTypes("canvas"),
    [AssetTypes.Chatbot]: tAssetTypes("chatbot"),
    [AssetTypes.Agent]: tAssetTypes("agent"),
    [AssetTypes.Interface]: tAssetTypes("form"),
  };
  const assetNames = assetTypes.map(
    (type) => labels[type] ?? tAssetTypes("item"),
  );
  const message =
    assetNames.length > 0
      ? t("named", {
          assetTypes: format.list(assetNames, { type: "conjunction" }),
        })
      : t("generic", { hasData: String(error.hasData) });

  return (
    <div role="alert" aria-live="polite">
      <Alert title={message} variant="default" status="error">
        <div className={styles.actions}>
          <Button
            variant="secondary"
            size="small"
            isDisabled={error.isRetrying}
            onPress={() => {
              void error.retry();
            }}
          >
            {t("retry")}
          </Button>
          {error.hasServiceError && (
            <Link
              href="https://status.zapier.com/"
              target="_blank"
              size="small"
            >
              {t("status")}
            </Link>
          )}
        </div>
      </Alert>
    </div>
  );
}
